import React, { useState, useEffect, useRef } from 'react'
import './DataDashboard.css'

// Static stats file path (generated by scripts/generate-dashboard-stats.mjs)
const STATS_URL = '/data/dashboard-stats.json'

// Animated counter hook - counts up from 0 to target value
function useAnimatedCounter(target, duration = 1000) {
  const [count, setCount] = useState(0)
  const startTimeRef = useRef(null)
  const rafRef = useRef(null)

  useEffect(() => {
    if (typeof target !== 'number' || target === 0) {
      setCount(target || 0)
      return
    }

    startTimeRef.current = performance.now()

    const animate = (currentTime) => {
      const elapsed = currentTime - startTimeRef.current
      const progress = Math.min(elapsed / duration, 1)

      // Ease out cubic for smooth deceleration
      const eased = 1 - Math.pow(1 - progress, 3)
      setCount(Math.floor(eased * target))

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(animate)
      } else {
        setCount(target)
      }
    }

    rafRef.current = requestAnimationFrame(animate)

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current)
      }
    }
  }, [target, duration])

  return count
}
// Stat Card Component with animated counter
function StatCard({ label, value, subtext, accent }) {
  const animatedValue = useAnimatedCounter(typeof value === 'number' ? value : 0, 1200)
  const displayValue = typeof value === 'number' ? animatedValue.toLocaleString() : value

  return (
    <div className="stat-card" style={accent ? { '--card-accent': accent } : undefined}>
      <div className="stat-value">{displayValue}</div>
      <div className="stat-label">{label}</div>
      {subtext && <div className="stat-subtext">{subtext}</div>}
    </div>
  )
}

// Bar Chart Component with animated bars
function BarChart({ data, maxValue, accentColor, showPercent }) {
  const [animated, setAnimated] = useState(false)
  const max = maxValue || Math.max(...data.map(d => d[1]))

  useEffect(() => {
    // Trigger animation after mount
    const timer = setTimeout(() => setAnimated(true), 50)
    return () => clearTimeout(timer)
  }, [])

  return (
    <div className="bar-chart">
      {data.map(([label, count], index) => (
        <div key={label} className="bar-row" style={{ '--bar-delay': `${index * 80}ms` }}>
          <span className="bar-label">{label}</span>
          <div className="bar-track">
            <div
              className={`bar-fill ${animated ? 'animated' : ''}`}
              style={{
                '--bar-width': `${(count / max) * 100}%`,
                background: accentColor || 'var(--app-accent)',
              }}
            />
          </div>
          <span className="bar-value">
            {count.toLocaleString()}
            {showPercent && <span className="bar-percent"> ({((count / max) * 100).toFixed(0)}%)</span>}
          </span>
        </div>
      ))}
    </div>
  )
}

// Status Breakdown Component with animated segments
function StatusBreakdown({ visited, unvisited, golden, total }) {
  const [animated, setAnimated] = useState(false)
  const pctVisited = total ? ((visited / total) * 100).toFixed(1) : 0
  const pctUnvisited = total ? ((unvisited / total) * 100).toFixed(1) : 0
  const pctGolden = total ? ((golden / total) * 100).toFixed(1) : 0

  const animatedVisited = useAnimatedCounter(visited, 1200)
  const animatedGolden = useAnimatedCounter(golden, 1200)
  const animatedUnvisited = useAnimatedCounter(unvisited, 1200)

  useEffect(() => {
    const timer = setTimeout(() => setAnimated(true), 50)
    return () => clearTimeout(timer)
  }, [])

  return (
    <div className="status-breakdown">
      <div className="status-bar">
        <div
          className={`status-segment visited ${animated ? 'animated' : ''}`}
          style={{ '--segment-width': `${pctVisited}%` }}
          title={`Visited: ${pctVisited}%`}
        />
        <div
          className={`status-segment golden ${animated ? 'animated' : ''}`}
          style={{ '--segment-width': `${pctGolden}%`, '--segment-delay': '150ms' }}
          title={`Golden: ${pctGolden}%`}
        />
        <div
          className={`status-segment unvisited ${animated ? 'animated' : ''}`}
          style={{ '--segment-width': `${pctUnvisited}%`, '--segment-delay': '300ms' }}
          title={`Unvisited: ${pctUnvisited}%`}
        />
      </div>
      <div className="status-legend">
        <span className="legend-item"><span className="dot visited" /> Visited ({animatedVisited.toLocaleString()})</span>
        <span className="legend-item"><span className="dot golden" /> Golden ({animatedGolden.toLocaleString()})</span>
        <span className="legend-item"><span className="dot unvisited" /> Unvisited ({animatedUnvisited.toLocaleString()})</span>
      </div>
    </div>
  )
}

// Data Quality Card Component
function DataQualityCard({ label, value, total, color }) {
  const [animated, setAnimated] = useState(false)
  const pct = total ? ((value / total) * 100) : 0
  const animatedValue = useAnimatedCounter(value, 1200)

  useEffect(() => {
    const timer = setTimeout(() => setAnimated(true), 50)
    return () => clearTimeout(timer)
  }, [])

  return (
    <div className="quality-card">
      <div className="quality-label">{label}</div>
      <div className="quality-bar-track">
        <div
          className={`quality-bar-fill ${animated ? 'animated' : ''}`}
          style={{ '--bar-width': `${pct}%`, background: color }}
        />
      </div>
      <div className="quality-value">
        {pct.toFixed(1)}% <span className="quality-count">({animatedValue.toLocaleString()} / {total.toLocaleString()})</span>
      </div>
    </div>
  )
}

// Rating Stat Card (shows average with star)
function RatingStatCard({ avgRating, ratedCount, accent }) {
  const [displayRating, setDisplayRating] = useState(0)

  useEffect(() => {
    if (avgRating === null) return
    const duration = 1200
    const startTime = performance.now()
    let rafId

    const animate = (currentTime) => {
      const elapsed = currentTime - startTime
      const progress = Math.min(elapsed / duration, 1)
      const eased = 1 - Math.pow(1 - progress, 3)
      setDisplayRating(eased * avgRating)

      if (progress < 1) {
        rafId = requestAnimationFrame(animate)
      } else {
        setDisplayRating(avgRating)
      }
    }

    rafId = requestAnimationFrame(animate)
    return () => cancelAnimationFrame(rafId)
  }, [avgRating])

  if (avgRating === null) {
    return (
      <div className="stat-card" style={accent ? { '--card-accent': accent } : undefined}>
        <div className="stat-value">-</div>
        <div className="stat-label">Avg Rating</div>
        <div className="stat-subtext">No ratings yet</div>
      </div>
    )
  }

  return (
    <div className="stat-card" style={accent ? { '--card-accent': accent } : undefined}>
      <div className="stat-value rating-value">
        <span className="rating-star">★</span> {displayRating.toFixed(1)}
      </div>
      <div className="stat-label">Avg Rating</div>
      <div className="stat-subtext">from {ratedCount.toLocaleString()} rated</div>
    </div>
  )
}

// Best Rated Styles Component
function BestRatedStyles({ styles, accentColor }) {
  const [animated, setAnimated] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => setAnimated(true), 50)
    return () => clearTimeout(timer)
  }, [])

  if (!styles.length) {
    return <p className="no-data">Not enough rated places by style yet</p>
  }

  return (
    <div className="best-rated-list">
      {styles.map(([style, avgRating, count], index) => (
        <div key={style} className="best-rated-item" style={{ '--item-delay': `${index * 100}ms` }}>
          <span className="best-rated-rank">#{index + 1}</span>
          <span className="best-rated-style">{style}</span>
          <span className={`best-rated-rating ${animated ? 'animated' : ''}`}>
            <span className="rating-star">★</span> {avgRating.toFixed(2)}
          </span>
          <span className="best-rated-count">({count} places)</span>
        </div>
      ))}
    </div>
  )
}

// Best Rated Regions Component (similar to BestRatedStyles but for regions)
function BestRatedRegions({ regions, accentColor }) {
  const [animated, setAnimated] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => setAnimated(true), 50)
    return () => clearTimeout(timer)
  }, [])

  if (!regions.length) {
    return <p className="no-data">Not enough rated regions yet (min 10 places)</p>
  }

  return (
    <div className="best-rated-list">
      {regions.map(([region, avgRating, count], index) => (
        <div key={region} className="best-rated-item" style={{ '--item-delay': `${index * 80}ms` }}>
          <span className="best-rated-rank">#{index + 1}</span>
          <span className="best-rated-style">{region}</span>
          <span className={`best-rated-rating ${animated ? 'animated' : ''}`}>
            {avgRating.toFixed(1)}/10
          </span>
          <span className="best-rated-count">({count} rated)</span>
        </div>
      ))}
    </div>
  )
}

// Country Breakdown Component
function CountryBreakdown({ byCountry, accentColor }) {
  const data = [
    // North America
    ['🇺🇸 United States', byCountry.US],
    ['🇨🇦 Canada', byCountry.CA],
    ['🇲🇽 Mexico', byCountry.MX],
    // Europe - Wave 1
    ['🇮🇹 Italy', byCountry.IT],
    ['🇩🇪 Germany', byCountry.DE],
    ['🇫🇷 France', byCountry.FR],
    ['🇪🇸 Spain', byCountry.ES],
    ['🇬🇧 United Kingdom', byCountry.GB],
    // Europe - Wave 2
    ['🇳🇱 Netherlands', byCountry.NL],
    ['🇧🇪 Belgium', byCountry.BE],
    ['🇦🇹 Austria', byCountry.AT],
    ['🇨🇭 Switzerland', byCountry.CH],
    ['🇵🇱 Poland', byCountry.PL],
    ['🇵🇹 Portugal', byCountry.PT],
    ['🇨🇿 Czech Republic', byCountry.CZ],
    ['🇸🇪 Sweden', byCountry.SE],
    ['🇳🇴 Norway', byCountry.NO],
    ['🇩🇰 Denmark', byCountry.DK],
    ['🇫🇮 Finland', byCountry.FI],
    ['🇮🇪 Ireland', byCountry.IE],
    ['🇬🇷 Greece', byCountry.GR],
    ['🇭🇺 Hungary', byCountry.HU],
    ['🇭🇷 Croatia', byCountry.HR],
    ['🇷🇴 Romania', byCountry.RO],
    // Latin America - South America
    ['🇧🇷 Brazil', byCountry.BR],
    ['🇦🇷 Argentina', byCountry.AR],
    ['🇨🇴 Colombia', byCountry.CO],
    ['🇨🇱 Chile', byCountry.CL],
    ['🇵🇪 Peru', byCountry.PE],
    ['🇻🇪 Venezuela', byCountry.VE],
    ['🇪🇨 Ecuador', byCountry.EC],
    ['🇧🇴 Bolivia', byCountry.BO],
    ['🇵🇾 Paraguay', byCountry.PY],
    ['🇺🇾 Uruguay', byCountry.UY],
    ['🇬🇾 Guyana', byCountry.GY],
    ['🇸🇷 Suriname', byCountry.SR],
    // Latin America - Central America
    ['🇬🇹 Guatemala', byCountry.GT],
    ['🇭🇳 Honduras', byCountry.HN],
    ['🇸🇻 El Salvador', byCountry.SV],
    ['🇳🇮 Nicaragua', byCountry.NI],
    ['🇨🇷 Costa Rica', byCountry.CR],
    ['🇵🇦 Panama', byCountry.PA],
    ['🇧🇿 Belize', byCountry.BZ],
  ].filter(([_, count]) => count > 0)
   .sort((a, b) => b[1] - a[1])

  if (data.length === 0) {
    return <p className="no-data">No geographic data available</p>
  }

  return <BarChart data={data} accentColor={accentColor} />
}

// Overview Tab
function OverviewTab({ pizzaStats, tacoStats }) {
  if (!pizzaStats || !tacoStats) return null

  const totalPlaces = pizzaStats.total + tacoStats.total
  const allStates = new Set([
    ...pizzaStats.byState.map(s => s[0]),
    ...tacoStats.byState.map(s => s[0]),
  ])

  // Combine country data
  const combinedByCountry = {
    US: pizzaStats.byCountry.US + tacoStats.byCountry.US,
    CA: pizzaStats.byCountry.CA + tacoStats.byCountry.CA,
    MX: pizzaStats.byCountry.MX + tacoStats.byCountry.MX,
    IT: pizzaStats.byCountry.IT + tacoStats.byCountry.IT,
    DE: pizzaStats.byCountry.DE + tacoStats.byCountry.DE,
    FR: pizzaStats.byCountry.FR + tacoStats.byCountry.FR,
    ES: pizzaStats.byCountry.ES + tacoStats.byCountry.ES,
    GB: pizzaStats.byCountry.GB + tacoStats.byCountry.GB,
    NL: pizzaStats.byCountry.NL + tacoStats.byCountry.NL,
    BE: pizzaStats.byCountry.BE + tacoStats.byCountry.BE,
    AT: pizzaStats.byCountry.AT + tacoStats.byCountry.AT,
    CH: pizzaStats.byCountry.CH + tacoStats.byCountry.CH,
    PL: pizzaStats.byCountry.PL + tacoStats.byCountry.PL,
    PT: pizzaStats.byCountry.PT + tacoStats.byCountry.PT,
    CZ: pizzaStats.byCountry.CZ + tacoStats.byCountry.CZ,
    SE: pizzaStats.byCountry.SE + tacoStats.byCountry.SE,
    NO: pizzaStats.byCountry.NO + tacoStats.byCountry.NO,
    DK: pizzaStats.byCountry.DK + tacoStats.byCountry.DK,
    FI: pizzaStats.byCountry.FI + tacoStats.byCountry.FI,
    IE: pizzaStats.byCountry.IE + tacoStats.byCountry.IE,
    GR: pizzaStats.byCountry.GR + tacoStats.byCountry.GR,
    HU: pizzaStats.byCountry.HU + tacoStats.byCountry.HU,
    HR: pizzaStats.byCountry.HR + tacoStats.byCountry.HR,
    RO: pizzaStats.byCountry.RO + tacoStats.byCountry.RO,
    // Latin America - South America
    BR: pizzaStats.byCountry.BR + tacoStats.byCountry.BR,
    AR: pizzaStats.byCountry.AR + tacoStats.byCountry.AR,
    CO: pizzaStats.byCountry.CO + tacoStats.byCountry.CO,
    CL: pizzaStats.byCountry.CL + tacoStats.byCountry.CL,
    PE: pizzaStats.byCountry.PE + tacoStats.byCountry.PE,
    VE: pizzaStats.byCountry.VE + tacoStats.byCountry.VE,
    EC: pizzaStats.byCountry.EC + tacoStats.byCountry.EC,
    BO: pizzaStats.byCountry.BO + tacoStats.byCountry.BO,
    PY: pizzaStats.byCountry.PY + tacoStats.byCountry.PY,
    UY: pizzaStats.byCountry.UY + tacoStats.byCountry.UY,
    GY: pizzaStats.byCountry.GY + tacoStats.byCountry.GY,
    SR: pizzaStats.byCountry.SR + tacoStats.byCountry.SR,
    // Latin America - Central America
    GT: pizzaStats.byCountry.GT + tacoStats.byCountry.GT,
    HN: pizzaStats.byCountry.HN + tacoStats.byCountry.HN,
    SV: pizzaStats.byCountry.SV + tacoStats.byCountry.SV,
    NI: pizzaStats.byCountry.NI + tacoStats.byCountry.NI,
    CR: pizzaStats.byCountry.CR + tacoStats.byCountry.CR,
    PA: pizzaStats.byCountry.PA + tacoStats.byCountry.PA,
    BZ: pizzaStats.byCountry.BZ + tacoStats.byCountry.BZ,
  }
  const countriesCount = Object.values(combinedByCountry).filter(c => c > 0).length

  return (
    <div className="tab-content">
      <div className="stats-row">
        <StatCard label="Total Places" value={totalPlaces} subtext="Pizza + Taco combined" />
        <StatCard label="Pizza Places" value={pizzaStats.total} accent="#f97316" />
        <StatCard label="Taco Places" value={tacoStats.total} accent="#b57c3b" />
        <StatCard label="Regions Covered" value={allStates.size} subtext={`across ${countriesCount} ${countriesCount === 1 ? 'country' : 'countries'}`} />
      </div>

      <div className="section">
        <h2>Coverage Comparison</h2>
        <div className="comparison-grid">
          <div className="comparison-card">
            <h3>Pizza Coverage</h3>
            <StatusBreakdown {...pizzaStats} total={pizzaStats.total} />
          </div>
          <div className="comparison-card">
            <h3>Taco Coverage</h3>
            <StatusBreakdown {...tacoStats} total={tacoStats.total} />
          </div>
        </div>
      </div>

      <div className="section">
        <h2>Data Quality Comparison</h2>
        <div className="comparison-grid">
          <div className="comparison-card">
            <h3>Pizza</h3>
            <DataQualityCard
              label="Has Address"
              value={pizzaStats.withAddress}
              total={pizzaStats.total}
              color="#f97316"
            />
          </div>
          <div className="comparison-card">
            <h3>Taco</h3>
            <DataQualityCard
              label="Has Address"
              value={tacoStats.withAddress}
              total={tacoStats.total}
              color="#b57c3b"
            />
          </div>
        </div>
      </div>

      <div className="section">
        <h2>Geographic Reach</h2>
        <CountryBreakdown byCountry={combinedByCountry} accentColor="var(--app-accent)" />
      </div>

      <div className="section">
        <h2>Top States (Combined)</h2>
        <div className="top-states-grid">
          <div>
            <h3>Pizza</h3>
            <BarChart data={pizzaStats.byState.slice(0, 5)} accentColor="#f97316" />
          </div>
          <div>
            <h3>Taco</h3>
            <BarChart data={tacoStats.byState.slice(0, 5)} accentColor="#b57c3b" />
          </div>
        </div>
      </div>
    </div>
  )
}

// Pizza Tab
function PizzaTab({ stats }) {
  if (!stats) return null

  const countriesCount = Object.values(stats.byCountry).filter(c => c > 0).length

  return (
    <div className="tab-content">
      <div className="stats-row">
        <StatCard label="Total Pizza Places" value={stats.total} accent="#f97316" />
        <StatCard label="Styles Tracked" value={stats.distribution.length} />
        <RatingStatCard avgRating={stats.avgRating} ratedCount={stats.ratedCount} accent="#f97316" />
        <StatCard label="Top Rated" value={stats.topRatedCount} subtext="9+ out of 10" accent="#facc15" />
      </div>

      <div className="section">
        <h2>Rating Analytics</h2>
        <div className="comparison-grid">
          <div className="comparison-card">
            <h3>Rating Distribution</h3>
            <BarChart
              data={['0-2', '3-4', '5-6', '7-8', '9-10+'].map((label, i) => [label, stats.ratingDistribution[i]])}
              accentColor="#facc15"
            />
          </div>
          <div className="comparison-card">
            <h3>Best Rated Styles</h3>
            <BestRatedStyles styles={stats.bestRatedStyles} accentColor="#f97316" />
          </div>
        </div>
      </div>

      <div className="section">
        <h2>Pizza Style Distribution</h2>
        <BarChart data={stats.distribution} accentColor="#f97316" />
      </div>

      <div className="section">
        <h2>Price Breakdown</h2>
        <BarChart data={stats.byPrice} accentColor="#f97316" />
      </div>

      <div className="section">
        <h2>Visit Status</h2>
        <StatusBreakdown {...stats} total={stats.total} />
      </div>

      <div className="section">
        <h2>Data Quality</h2>
        <div className="quality-grid">
          <DataQualityCard
            label="Has Address (All Places)"
            value={stats.withAddress}
            total={stats.total}
            color="#f97316"
          />
          <DataQualityCard
            label="Reviewed Complete (of visited)"
            value={stats.withAllFields}
            total={stats.visitedOrGolden}
            color="#22c55e"
          />
        </div>
      </div>

      <div className="section">
        <h2>Geographic Reach ({countriesCount} {countriesCount === 1 ? 'country' : 'countries'})</h2>
        <CountryBreakdown byCountry={stats.byCountry} accentColor="#f97316" />
      </div>

      <div className="section">
        <h2>Top 10 Regions</h2>
        <div className="comparison-grid">
          <div className="comparison-card">
            <h3>By Place Count</h3>
            <BarChart data={stats.byState} accentColor="#f97316" />
          </div>
          <div className="comparison-card">
            <h3>By Average Rating</h3>
            <BestRatedRegions regions={stats.bestRatedRegions} accentColor="#f97316" />
          </div>
        </div>
      </div>

      {stats.byCity.length > 0 && (
        <div className="section">
          <h2>Top 10 Cities</h2>
          <BarChart data={stats.byCity} accentColor="#f97316" />
        </div>
      )}
    </div>
  )
}

// Taco Tab
function TacoTab({ stats }) {
  if (!stats) return null

  const countriesCount = Object.values(stats.byCountry).filter(c => c > 0).length

  return (
    <div className="tab-content">
      <div className="stats-row">
        <StatCard label="Total Taco Places" value={stats.total} accent="#b57c3b" />
        <StatCard label="Protein Types" value={stats.distribution.length} />
        <RatingStatCard avgRating={stats.avgRating} ratedCount={stats.ratedCount} accent="#b57c3b" />
        <StatCard label="Top Rated" value={stats.topRatedCount} subtext="9+ out of 10" accent="#facc15" />
      </div>

      <div className="section">
        <h2>Rating Analytics</h2>
        <div className="comparison-grid">
          <div className="comparison-card">
            <h3>Rating Distribution</h3>
            <BarChart
              data={['0-2', '3-4', '5-6', '7-8', '9-10+'].map((label, i) => [label, stats.ratingDistribution[i]])}
              accentColor="#facc15"
            />
          </div>
          <div className="comparison-card">
            <h3>Best Rated Types</h3>
            <BestRatedStyles styles={stats.bestRatedStyles} accentColor="#b57c3b" />
          </div>
        </div>
      </div>

      <div className="section">
        <h2>Taco Type Distribution</h2>
        <BarChart data={stats.distribution} accentColor="#b57c3b" />
      </div>

      <div className="section">
        <h2>Price Breakdown</h2>
        <BarChart data={stats.byPrice} accentColor="#b57c3b" />
      </div>

      <div className="section">
        <h2>Visit Status</h2>
        <StatusBreakdown {...stats} total={stats.total} />
      </div>

      <div className="section">
        <h2>Data Quality</h2>
        <div className="quality-grid">
          <DataQualityCard
            label="Has Address (All Places)"
            value={stats.withAddress}
            total={stats.total}
            color="#b57c3b"
          />
          <DataQualityCard
            label="Reviewed Complete (of visited)"
            value={stats.withAllFields}
            total={stats.visitedOrGolden}
            color="#22c55e"
          />
        </div>
      </div>

      <div className="section">
        <h2>Geographic Reach ({countriesCount} {countriesCount === 1 ? 'country' : 'countries'})</h2>
        <CountryBreakdown byCountry={stats.byCountry} accentColor="#b57c3b" />
      </div>

      <div className="section">
        <h2>Top 10 Regions</h2>
        <div className="comparison-grid">
          <div className="comparison-card">
            <h3>By Place Count</h3>
            <BarChart data={stats.byState} accentColor="#b57c3b" />
          </div>
          <div className="comparison-card">
            <h3>By Average Rating</h3>
            <BestRatedRegions regions={stats.bestRatedRegions} accentColor="#b57c3b" />
          </div>
        </div>
      </div>

      {stats.byCity.length > 0 && (
        <div className="section">
          <h2>Top 10 Cities</h2>
          <BarChart data={stats.byCity} accentColor="#b57c3b" />
        </div>
      )}
    </div>
  )
}

// Main Dashboard Component
export default function DataDashboard() {
  const [activeTab, setActiveTab] = useState('overview')
  const [pizzaStats, setPizzaStats] = useState(null)
  const [tacoStats, setTacoStats] = useState(null)
  const [generatedAt, setGeneratedAt] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    async function loadStats() {
      setLoading(true)
      setError(null)

      try {
        // Load pre-computed stats from static JSON (zero egress!)
        const response = await fetch(STATS_URL)
        if (!response.ok) {
          throw new Error(`Failed to load stats: ${response.status}`)
        }

        const data = await response.json()
        setPizzaStats(data.pizza)
        setTacoStats(data.taco)
        setGeneratedAt(data.generatedAt)
      } catch (err) {
        console.error('Failed to load dashboard stats:', err)
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }

    loadStats()
  }, [])

  if (loading) {
    return (
      <div className="data-dashboard">
        <div className="loading-state">
          <div className="spinner" />
          <p>Loading dashboard data...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="data-dashboard">
        <div className="error-state">
          <p>Failed to load data: {error}</p>
          <button onClick={() => window.location.reload()}>Retry</button>
        </div>
      </div>
    )
  }

  return (
    <div className="data-dashboard">
      <header className="dashboard-header">
        <h1>Data Dashboard</h1>
        <p className="dashboard-subtitle">Pizza and taco place statistics worldwide</p>
      </header>

      <nav className="dashboard-tabs">
        {['overview', 'pizza', 'taco'].map(tab => (
          <button
            key={tab}
            className={`dashboard-tab ${activeTab === tab ? 'active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </nav>

      <main className="dashboard-content">
        {activeTab === 'overview' && <OverviewTab pizzaStats={pizzaStats} tacoStats={tacoStats} />}
        {activeTab === 'pizza' && <PizzaTab stats={pizzaStats} />}
        {activeTab === 'taco' && <TacoTab stats={tacoStats} />}
      </main>

      <footer className="dashboard-footer">
        <a href="/">Back to Map</a>
        {generatedAt && (
          <span className="stats-date">
            Stats updated: {new Date(generatedAt).toLocaleDateString()}
          </span>
        )}
      </footer>
    </div>
  )
}
